import * as z from 'zod/mini';
import {
  FLOOR_NAME_MAX_LENGTH,
  LATITUDE_MAX_DEGREES,
  LONGITUDE_MAX_DEGREES,
  PLAN_SHAPE_LABEL_MAX_LENGTH,
  PLAN_SHAPE_MAX_POINTS,
  PLAN_UNITS_HIGH,
  PLAN_UNITS_WIDE,
} from '../constants.ts';
import { itemTypeSchema } from '../item-types.ts';
import { idSchema, timestampSchema } from './common.ts';

// The floor-plan drawing model (migration 0044). A property has floors; each
// floor is a drawing space of its own — an optional underlay image, a scale,
// and the vector shapes drawn on it. A shape is a room outline, a wall or
// cable-run line, an outlet/camera/AP/device marker, or a freehand/text
// markup. Points are stored in the floor's abstract unit space (never
// pixels), so a shape reads the same on a phone, a desktop, and a printed
// page, exactly as a plan pin is a fraction of its image (shared/schemas/
// plan.ts). A room or marker may name the node it stands for; the row keeps
// only the id, and the node's own name is read back when the shapes are
// listed, so a renamed device is renamed on the plan too.

/** The real-world unit a floor's scale is measured in. */
export const PLAN_SCALE_UNITS = ['ft', 'm'] as const;
export const planScaleUnitSchema = z.enum(PLAN_SCALE_UNITS);

/** A floor's scale: how many real-world units one plan unit is worth. */
export const planScaleSchema = z.object({
  unit: planScaleUnitSchema,
  factor: z
    .number()
    .check(z.gt(0, 'A scale is a length greater than zero'), z.lte(Number.MAX_SAFE_INTEGER)),
});
export type PlanScale = z.infer<typeof planScaleSchema>;

/**
 * What a floor draws (ROADMAP D12, migration 0066): a storey of the building,
 * or the site itself — an aerial or campus picture with the outdoor equipment
 * placed on it. A site is a floor in every other way: the same underlay, the
 * same scale, the same outlines, markers, lines, and printed drawing.
 */
export const FLOOR_KINDS = ['floor', 'site'] as const;
export type FloorKind = (typeof FLOOR_KINDS)[number];
export const floorKindSchema = z.enum(FLOOR_KINDS);

/** What each floor kind is called wherever one is named or chosen. */
export const FLOOR_KIND_LABELS: Readonly<Record<FloorKind, string>> = {
  floor: 'Floor',
  site: 'Site',
};

/**
 * What a shape is: an area, a wall, a placed marker, or one of the markups
 * (ROADMAP D8, migration 0067) — a typed note, a freehand line, an arrow with
 * a head on its last point, or a revision cloud round what changed. A markup
 * says something about the plan; it names no node and moves no device.
 */
export const PLAN_SHAPE_KINDS = [
  'room',
  'wall',
  'marker',
  'note',
  'line',
  'arrow',
  'cloud',
] as const;
export type PlanShapeKind = (typeof PLAN_SHAPE_KINDS)[number];
export const planShapeKindSchema = z.enum(PLAN_SHAPE_KINDS);

/** The markup kinds: the shapes that annotate a plan rather than record it (D8). */
export const PLAN_MARKUP_KINDS = ['note', 'line', 'arrow', 'cloud'] as const;
export type PlanMarkupKind = (typeof PLAN_MARKUP_KINDS)[number];

/** Whether a shape kind is a markup, so it is drawn on its own printed layer (D8). */
export function isMarkupKind(kind: PlanShapeKind): kind is PlanMarkupKind {
  return (PLAN_MARKUP_KINDS as readonly string[]).includes(kind);
}

/**
 * What a marker draws: a wall outlet, a camera, an access point, a cable drop,
 * a device, or a punch pin — a task or a deficiency dropped where it is, its
 * words in the shape's own `label` (ROADMAP D8).
 */
export const PLAN_MARKER_KINDS = ['outlet', 'camera', 'ap', 'drop', 'device', 'punch'] as const;
export type PlanMarkerKind = (typeof PLAN_MARKER_KINDS)[number];
export const planMarkerKindSchema = z.enum(PLAN_MARKER_KINDS);

/** What a shape can name: a room it outlines, or a device/rack/run its marker stands for. */
export const PLAN_SHAPE_NODE_TYPES = ['room', 'item', 'rack', 'drop'] as const;
export type PlanShapeNodeType = (typeof PLAN_SHAPE_NODE_TYPES)[number];
export const planShapeNodeTypeSchema = z.enum(PLAN_SHAPE_NODE_TYPES);

/** One axis of a point, held inside the floor's own unit space. */
const coordinate = (max: number) =>
  z.number().check(z.gte(0, 'A point sits on the floor'), z.lte(max, 'A point sits on the floor'));

/** A point in a floor's plan-unit space. */
export const planPointSchema = z.object({
  x: coordinate(PLAN_UNITS_WIDE),
  y: coordinate(PLAN_UNITS_HIGH),
});
export type PlanPointValue = z.infer<typeof planPointSchema>;

/** A shape's geometry: its ordered points, at least one and never unbounded. */
export const planPointsSchema = z
  .array(planPointSchema)
  .check(z.minLength(1, 'A shape has a point'), z.maxLength(PLAN_SHAPE_MAX_POINTS));

/**
 * One of the two points a floor is aligned by (ROADMAP D10, migration 0069):
 * somewhere on the plan, and where that place is on the earth.
 */
export const planAnchorSchema = z.object({
  point: planPointSchema,
  latitude: z
    .number()
    .check(z.gte(-LATITUDE_MAX_DEGREES), z.lte(LATITUDE_MAX_DEGREES), z.refine(Number.isFinite)),
  longitude: z
    .number()
    .check(z.gte(-LONGITUDE_MAX_DEGREES), z.lte(LONGITUDE_MAX_DEGREES), z.refine(Number.isFinite)),
});

/**
 * A floor's place on the earth: two points, which fix the rotation, the scale
 * and the offset of the whole plan. Two is exactly enough for a flat drawing
 * at one scale, and no more than enough: a third would let the app average
 * away a mistake instead of saying the two disagree.
 */
export const floorAlignmentSchema = z.object({ a: planAnchorSchema, b: planAnchorSchema });
export type FloorAlignment = z.infer<typeof floorAlignmentSchema>;

/** A floor's name: trimmed, never blank, and short enough for the column's CHECK. */
export const floorNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(FLOOR_NAME_MAX_LENGTH));

const shapeLabelSchema = z.string().check(z.trim(), z.maxLength(PLAN_SHAPE_LABEL_MAX_LENGTH));

export const floorSchema = z.object({
  id: idSchema,
  propertyId: idSchema,
  name: z.string(),
  /** A storey of the building, or the site itself (D12); defaulted for older caches. */
  kind: z._default(floorKindSchema, 'floor'),
  /** Which storey this is: a signed level (a basement is negative), for ordering by height. */
  level: z.int(),
  /** Where the floor sits among its siblings, lowest first. */
  sortOrder: z.int(),
  /** The plan image drawn under the shapes, if one was set; null draws a blank sheet. */
  underlayAttachmentId: z.nullable(idSchema),
  /** The width of the floor's drawing space, in abstract plan units. */
  width: z.int(),
  /** The height of the floor's drawing space, in abstract plan units. */
  height: z.int(),
  /** The floor's scale once a known distance was measured; null until then. */
  scale: z.nullable(planScaleSchema),
  /** Where this floor sits on the earth (D10), or null while nobody has aligned it. */
  alignment: z._default(z.nullable(floorAlignmentSchema), null),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Floor = z.infer<typeof floorSchema>;

export const planShapeSchema = z.object({
  id: idSchema,
  floorId: idSchema,
  kind: planShapeKindSchema,
  points: planPointsSchema,
  /** The node this shape stands for (a room it outlines, a device its marker names), or null. */
  nodeType: z.nullable(planShapeNodeTypeSchema),
  nodeId: z.nullable(idSchema),
  /** For a marker, what it draws; null for a room, wall, or note. */
  markerKind: z.nullable(planMarkerKindSchema),
  /** A markup's own text, a punch pin's task or deficiency, or a label typed on a shape. */
  label: z.string(),
  /** The checklist step a punch pin stands for (D8); null when it stands alone. */
  checklistItemId: z._default(z.nullable(idSchema), null),
  /** That step's words as they read right now; '' when the pin names no step, or the step is gone. */
  checklistItemText: z._default(z.string(), ''),
  /** Draw order within the floor, lowest first. */
  z: z.int(),
  /** The linked node's name as it reads right now; '' when the shape names nothing, or the node is gone. */
  nodeName: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type PlanShape = z.infer<typeof planShapeSchema>;

/** POST /api/properties/:id/floors: a new floor takes a name and its level. */
export const floorInputSchema = z.strictObject({
  name: z.optional(floorNameSchema),
  level: z.optional(z.int()),
  /** A storey (the default) or the site's own picture (D12). */
  kind: z.optional(floorKindSchema),
});
export type FloorInput = z.infer<typeof floorInputSchema>;

/** PATCH /api/floors/:id: any of the floor's own facts, each optional. */
export const floorPatchSchema = z.strictObject({
  name: z.optional(floorNameSchema),
  kind: z.optional(floorKindSchema),
  level: z.optional(z.int()),
  sortOrder: z.optional(z.int()),
  underlayAttachmentId: z.optional(z.nullable(idSchema)),
  scale: z.optional(z.nullable(planScaleSchema)),
  /** The two points that place this floor on the map (D10); null clears it. */
  alignment: z.optional(z.nullable(floorAlignmentSchema)),
});
export type FloorPatch = z.infer<typeof floorPatchSchema>;

/** PUT /api/properties/:id/floors/order: the floors in the order they should read. */
export const floorOrderSchema = z.strictObject({
  ids: z.array(idSchema).check(z.minLength(1, 'An order names its floors')),
});
export type FloorOrder = z.infer<typeof floorOrderSchema>;

/** The node link a shape may carry, both ends present or both absent. */
const shapeLink = {
  nodeType: z.optional(z.nullable(planShapeNodeTypeSchema)),
  nodeId: z.optional(z.nullable(idSchema)),
  markerKind: z.optional(z.nullable(planMarkerKindSchema)),
  /** A punch pin's checklist step (D8); a step of one of the property's own lists. */
  checklistItemId: z.optional(z.nullable(idSchema)),
};

/** POST /api/floors/:id/shapes: a shape's kind, its points, and what it names. */
export const planShapeInputSchema = z.strictObject({
  kind: planShapeKindSchema,
  points: planPointsSchema,
  label: z.optional(shapeLabelSchema),
  z: z.optional(z.int()),
  /**
   * The device type to create on drop (ROADMAP D7): when a marker with no node
   * of its own carries a type, the route raises a real device of that type in
   * the room under the marker and links to it, generalising the outlet-on-drop
   * of D6. Request-only — nothing about the created device is stored on the
   * shape, only the resulting node link.
   */
  createType: z.optional(itemTypeSchema),
  /**
   * Where a marker sits inside more than one room outline (ROADMAP D3), the
   * room its device files in — the app asks, and this is the answer. It must
   * be one of the rooms enclosing the point; without it the topmost outline
   * wins. Request-only, like `createType`.
   */
  roomId: z.optional(idSchema),
  ...shapeLink,
});
export type PlanShapeInput = z.infer<typeof planShapeInputSchema>;

/** PATCH /api/shapes/:id: the geometry, label, order, or link of one shape. */
export const planShapePatchSchema = z.strictObject({
  points: z.optional(planPointsSchema),
  label: z.optional(shapeLabelSchema),
  z: z.optional(z.int()),
  /** As on a create: the room chosen where outlines overlap the moved marker. */
  roomId: z.optional(idSchema),
  ...shapeLink,
});
export type PlanShapePatch = z.infer<typeof planShapePatchSchema>;

export const floorListSchema = z.object({ floors: z.array(floorSchema) });
export const floorEnvelopeSchema = z.object({ floor: floorSchema });
export const planShapeListSchema = z.object({ shapes: z.array(planShapeSchema) });
export const planShapeEnvelopeSchema = z.object({
  shape: planShapeSchema,
  /** How many devices a redrawn room outline moved into the rooms their markers now sit in (D3); 0 on any other write. */
  rehomed: z._default(z.int(), 0),
  /** How many stayed put because their short ID is already used in the room they would have moved to. */
  held: z._default(z.int(), 0),
});

/**
 * A shape names a node when it carries both a node type and a node id, and a
 * marker also carries what it draws. A partial link — one end without the
 * other — is a defect the route refuses, so a marker never dangles half
 * attached.
 */
export function shapeLinkIsWhole(link: {
  nodeType?: PlanShapeNodeType | null | undefined;
  nodeId?: string | null | undefined;
}): boolean {
  return (link.nodeType == null) === (link.nodeId == null);
}
